import { useEffect, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";

const THEMES = ["system", "dark", "light"] as const;
type Theme = (typeof THEMES)[number];

const LABELS: Record<Theme, string> = {
  system: "🖥️",
  dark: "🌙",
  light: "☀️",
};

const COOKIE = "swz_theme";
const ONE_YEAR = 60 * 60 * 24 * 365;

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const effective: "dark" | "light" =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: light)").matches
        ? "light"
        : "dark"
      : theme;
  document.documentElement.dataset.theme = effective;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match && match[1] !== undefined ? decodeURIComponent(match[1]) : null;
}

/**
 * Sito theme toggle. Ports pattern from dashboard `src/components/ThemeSwitcher.jsx`.
 * Persists to cookie + localStorage. Reads `prefers-color-scheme` for `system`.
 */
export default function ThemeSwitcher() {
  const { t } = useI18n();
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === "undefined") return "dark";
    const stored = readCookie(COOKIE) || (typeof localStorage !== "undefined" ? localStorage.getItem(COOKIE) : null);
    if (stored && (THEMES as readonly string[]).includes(stored)) return stored as Theme;
    return "dark";
  });

  useEffect(() => {
    const stored = (readCookie(COOKIE) || localStorage.getItem(COOKIE) || "dark") as Theme;
    const valid: Theme = (THEMES as readonly string[]).includes(stored) ? stored : "dark";
    setTheme(valid);
    applyTheme(valid);

    if (valid === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: light)");
      const handler = () => applyTheme("system");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, []);

  function cycle() {
    const next = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length] ?? "dark";
    setTheme(next);
    applyTheme(next);
    document.cookie = `${COOKIE}=${next}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
    try {
      localStorage.setItem(COOKIE, next);
    } catch {
      // ignore (private mode / disabled storage)
    }
  }

  const title = t("site.theme.title", { theme });
  const aria = t("site.theme.aria_label", { theme });

  return (
    <button
      type="button"
      onClick={cycle}
      title={title}
      aria-label={aria}
      className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full border border-border/60 text-muted-foreground hover:border-primary hover:text-primary transition-colors text-base"
    >
      <span aria-hidden="true">{LABELS[theme]}</span>
    </button>
  );
}
